import React from "react";
import { isSimpleData, } from "./utils";
import isEqual from "lodash/fp/isEqual";
import { Clock } from "./clock";
import { rerenderHoc } from "./rerender-hoc";
// tslint:disable: jsx-no-multiline-js
/**
 * 抽象时间轴的上下文
 * 提供的数据和抽象时间轴的renderprops的参数一样
 * 用在高频刷新的部分，来增加性能
 * @author James Zhang
 */
// @ts-ignore 初始值只能在provider中提供
// tslint:disable: no-object-literal-type-assertion
// tslint:disable-next-line: variable-name
export const AbstractTimelineContext = React.createContext({});
/** 默认值 */
const stateValue = {
    /** 播放状态 */
    play: false,
    /**
     * 过去的时间
     *
     * 更新频率极高，如无必要，不要使用此状态，否则会导致频繁更新
     *
     */
    elapses: [],
    /** 用户点击行为禁止 */
    userClickforbiden: false,
    /** 当前选中的时间的id */
    key: "",
    /** 按年份分组后的时间 */
    dateGrouping: [],
    /**
     * 播放状态
     *
     * 是播放时间的简化版，用于无动画高性能场景
     *
     * @type {PlayStates}
     * @memberof ComputedData
     */
    playStates: [],
};
/**
 * 抽象时间轴
 *
 * 在任何时候，有且只有一个时间正在播放中
 * @author James Zhang
 * @author yangyongheng
 * @export
 */
class AbstractTimelineMain extends React.PureComponent {
    constructor(props) {
        super(props);
        /** 发出onChange事件，对发出的`key`做缓存，避免重复调用
         * @author James Zhang
         */
        this.dispatchChangeEvent = (key) => {
            // tslint:disable-next-line: no-collapsible-if
            if (this.props.onChange) {
                if (key !== this.eventKeyCache) {
                    this.eventKeyCache = key;
                    this.props.onChange(key);
                }
            }
        };
        /**
         * 更新状态
         */
        this.updateState = () => {
            const mergedState = this.getMergedState(this.props);
            this.setState({
                userClickforbiden: true,
                play: mergedState.play,
                key: mergedState.key,
            }, () => this.dispatchChangeEvent(mergedState.key));
        };
        /**
         * 选择日期
         *
         * 当 `key` 为年时，`key`会自动变成此年的第一个日期的`key`
         *
         * @memberof AbstractTimeline
         */
        this.changeDateByKey = (key) => {
            // tslint:disable-next-line: early-exit
            if (this.props.selected === undefined || this.props.selected !== key) {
                const { yearIndex, dayIndex } = this.getIndexByKey(key);
                this.setState(Object.assign({ key: this.state.dateGrouping[yearIndex].children[dayIndex].key }, this.getDateGrouping(this.props.data, yearIndex, dayIndex)), () => {
                    this.pause();
                    this.clock.reset();
                    this.dispatchChangeEvent(this.state.key);
                });
            }
        };
        /** 根据唯一标志计算索引 */
        this.getIndexByKey = (key) => {
            let yearIndex = 0;
            let dayIndex = 0;
            if (key === undefined) {
                return {
                    yearIndex: 0,
                    dayIndex: 0,
                };
            }
            this.state.dateGrouping.forEach((item, index) => {
                if (item.key === key) {
                    yearIndex = index;
                    return;
                }
                const target = item.children.find(item2 => item2.key === key);
                if (target) {
                    dayIndex = item.children.indexOf(target);
                    yearIndex = index;
                }
            });
            return { yearIndex, dayIndex };
        };
        /**
         * 播放
         *
         * @memberof AbstractTimeline
         */
        this.pause = () => {
            this.setState({ play: false });
            this.clock.pause();
            this.props.onClickPause && this.props.onClickPause();
        };
        /**
         * 暂停
         *
         * @memberof AbstractTimeline
         */
        this.play = () => {
            this.setState({ play: true });
            this.clock.play();
            this.props.onClickPlay && this.props.onClickPlay();
        };
        /**
         * 时间步进
         *
         * 这是所有组件的核心逻辑
         *
         * @author 张卓诚
         *
         * @memberof AbstractTimeline
         */
        this.step = (event, elapse, time) => {
            if (event === "play") {
                const { yearIndex, dayIndex } = this.getIndexByKey(this.state.key);
                this.state.playStates[yearIndex].children[dayIndex].playState = "playing";
                this.state.playStates[yearIndex].playState = "playing";
            }
            else if (event === "elapse") {
                const { yearIndex, dayIndex } = this.getIndexByKey(this.state.key);
                this.state.elapses[yearIndex].children[dayIndex].elapse = elapse;
                this.state.elapses[yearIndex].elapse = this.state.elapses[yearIndex].children
                    .map(item => item.elapse).reduce((item1, item2) => item1 + item2, 0);
                this.state.elapses[yearIndex].total = this.state.elapses[yearIndex].children
                    .map(item => item.total).reduce((item1, item2) => item1 + item2, 0);
                this.setState({ elapses: [...this.state.elapses] });
            }
            else if (event === "stop") {
                const { yearIndex, dayIndex } = this.getIndexByKey(this.state.key);
                this.state.elapses[yearIndex].children[dayIndex].elapse = elapse;
                this.state.elapses[yearIndex].elapse = this.state.elapses[yearIndex].children
                    .map(item => item.elapse).reduce((item1, item2) => item1 + item2, 0);
                this.state.elapses[yearIndex].total = this.state.elapses[yearIndex].children
                    .map(item => item.total).reduce((item1, item2) => item1 + item2, 0);
                if (dayIndex !== this.state.dateGrouping[yearIndex].children.length - 1) {
                    // 播放完一个时间
                    const playStates = this.getPlayStates(this.state.elapses);
                    playStates[yearIndex].children[dayIndex + 1].playState = "playing";
                    this.setState({
                        playStates,
                        elapses: [...this.state.elapses],
                        key: this.state.dateGrouping[yearIndex].children[dayIndex + 1].key,
                    }, this.replayClcok);
                    // tslint:disable-next-line: no-collapsible-if
                }
                else {
                    if (this.props.loop === "no") {
                        /** 不循环 播放到最后一个时间 暂停 */
                        this.pause();
                    }
                    else if (this.props.loop === "year") {
                        /** 年内循环 播放到最后一个时间 初始化 */
                        const { elapses, playStates } = this.getDateGrouping(this.props.data, yearIndex, 0);
                        this.setState({
                            elapses,
                            playStates,
                            key: this.state.dateGrouping[yearIndex].children[0].key,
                        }, this.replayClcok);
                    }
                    else if (this.props.loop === "all") {
                        const newYearIndex = yearIndex < (this.state.dateGrouping.length - 1) ? yearIndex + 1 : 0;
                        const { elapses, playStates } = this.getDateGrouping(this.props.data, newYearIndex, 0);
                        this.setState({
                            elapses,
                            playStates,
                            key: this.state.dateGrouping[newYearIndex].children[0].key,
                        }, this.replayClcok);
                    }
                }
            }
        };
        /** 重播时钟 */
        this.replayClcok = () => {
            this.clock.reset();
            this.clock.play();
            this.dispatchChangeEvent(this.state.key);
        };
        // 初始化
        const initialYearIndex = 0;
        const initialDayIndex = 0;
        const { dateGrouping, elapses, playStates } = this.getDateGrouping(this.props.data, initialYearIndex, initialDayIndex);
        this.state = Object.assign({}, stateValue, { dateGrouping,
            elapses,
            playStates, play: this.props.defaultPlay !== undefined ? this.props.defaultPlay : stateValue.play, key: dateGrouping[initialYearIndex].children[initialDayIndex].key });
        this.clock = new Clock(this.props.interval);
        this.clock.subscribe(this.step);
        // 读取props中的作为受控组件的参数，再次初始化
        const mergedState = this.getMergedState(this.props);
        // 优先级排序： props.selected > props.defaultSelected > state.key > (initialYearIndex,initialDayIndex)
        const key = props.selected !== undefined ? props.selected
            : props.defaultSelected !== undefined ? props.defaultSelected
                : mergedState.key !== undefined ? mergedState.key
                    : dateGrouping[initialYearIndex].children[initialDayIndex].key;
        const { yearIndex, dayIndex } = this.getIndexByKey(key);
        const { dateGrouping: dateGrouping2, elapses: elapses2, playStates: playStates2, } = this.getDateGrouping(this.props.data, yearIndex, dayIndex);
        this.state = Object.assign({}, this.state, { key, play: mergedState.play, dateGrouping: dateGrouping2, elapses: elapses2, playStates: playStates2 });
        if (this.state.play) {
            this.clock.play();
        }
        this.dispatchChangeEvent(this.state.key);
    }
    componentDidUpdate(preProps) {
        // tslint:disable-next-line: early-exit
        if (!isEqual(preProps, this.props)) {
            const { yearIndex, dayIndex } = this.getIndexByKey(this.state.key);
            // `this.props.data`改变的时候，`rerenderHoc`已经使此组件已经重新实例化，所以可以假设`this.props.data`永远不变
            // 也就可以假设`yearIndex`和`dayIndex`是可以继续使用，无需清0
            const { dateGrouping, elapses, playStates } = this.getDateGrouping(this.props.data, yearIndex, dayIndex);
            this.setState({
                dateGrouping,
                elapses,
                playStates,
            }, this.updateState);
        }
    }
    // ====================数据初始化=================
    /**
     * data数据初始化
     * @private
     * @param {TimelineProps["type"]} type
     * @param {TimelineProps["data"]} data
     */
    getDateGrouping(data, initialYearIndex, initialDayIndex) {
        // 校验传入数据是否合法
        if (Array.isArray(data) && data.length && isSimpleData(data)) {
            /** data 数据预处理格式 */
            const { dateGrouping } = this.dateFormate(data);
            const elapses = dateGrouping.map(item => {
                return {
                    key: item.key,
                    elapse: 0,
                    // @ts-ignore
                    total: item.children.length * this.props.interval,
                    children: item.children.map(item2 => {
                        return {
                            key: item2.key,
                            elapse: 0,
                            // @ts-ignore
                            total: this.props.interval,
                        };
                    }),
                };
            });
            const playStates = this.getPlayStates(elapses);
            playStates[initialYearIndex].playState = "playing";
            playStates[initialYearIndex].children[initialDayIndex].playState = "playing";
            return {
                dateGrouping,
                elapses,
                playStates,
            };
        }
        throw new Error("数据格式不符合规范");
    }
    /**
     * 获取props和state合并后的state
     *
     * 实现props优先级比state优先级更高的受控组件模式
     *
     * @author 张卓诚
     * @returns
     * @memberof AbstractTimeline
     */
    getMergedState(props) {
        const selectedKey = this.getIndexAndKeyBySelected(props.selected).key;
        return Object.assign({}, this.state, { play: props.play !== undefined ? props.play : this.state.play, key: props.selected !== undefined ? selectedKey : this.state.key });
    }
    /**
     * 数据预处理
     * @private
     * @param {TimelineProps["data"]} data
     */
    dateFormate(data) {
        let dates = [];
        if (isSimpleData(data)) {
            dates = dateFormat(data);
        }
        // 过滤年份
        const yearsFilter = this.heavy(dates.map(item => item.year));
        /** years 过滤 */
        const years = yearsFilter.map((item, index) => ({
            key: item,
            year: item,
        }));
        // 根据年份分组成 {"2017": [...]}
        const dateGrouping = years.map(year => (Object.assign({}, year, { month: "", day: "", origin: year.key, children: dates
                .filter(item => item.year === year.year)
                .map(item => item) })));
        return { dates, dateGrouping, years };
    }
    /**
     * 根据selected获取在一维数组下的索引
     */
    getIndexAndKeyBySelected(selected) {
        let yearsIndex = 0;
        let dayIndex = 0;
        let key = this.state.dateGrouping[yearsIndex].children[dayIndex].key;
        if (selected === undefined) {
            return {
                yearsIndex,
                dayIndex,
                key,
            };
        }
        this.state.dateGrouping.forEach((item, index) => {
            const target = item.children.find(item2 => item2.origin === selected);
            if (target) {
                key = target.key;
                dayIndex = item.children.indexOf(target);
                yearsIndex = index;
            }
        });
        return {
            yearsIndex,
            dayIndex,
            key,
        };
    }
    /**
     * 获取所有播放状态
     *
     * @author 张卓诚
     * @returns {PlayStates}
     * @memberof AbstractTimeline
     */
    getPlayStates(elapses) {
        return elapses.map(item => {
            return Object.assign({}, this.getPlayState(item), { children: item.children.map(item2 => {
                    return this.getPlayState(item2);
                }) });
        });
    }
    /**
     * 将一个elapse转换成播放状态
     *
     * @author 张卓诚
     * @param {Elapse} elapse
     * @returns {PlayState["playState"]}
     * @memberof AbstractTimeline
     */
    getPlayState(elapse) {
        const value = elapse.total - elapse.elapse;
        if (value === elapse.total) {
            return {
                key: elapse.key,
                playState: "wait",
            };
        }
        if (value > 0) {
            return {
                key: elapse.key,
                playState: "playing",
            };
        }
        return {
            key: elapse.key,
            playState: "played",
        };
    }
    /**
     * 数组去重
     * @param {T[]} arg
     * @returns {T[]}
     */
    heavy(arg) {
        return [...new Set(arg)];
    }
    render() {
        // console.log(this.state.key);
        const { yearIndex, dayIndex } = this.getIndexByKey(this.state.key);
        // console.log(this.state.elapses);
        // console.log(this.state.playStates);
        return (React.createElement(React.Fragment, null,
            React.createElement(AbstractTimelineContext.Provider, { value: Object.assign({ letsPlay: this.play, pause: this.pause, changeDateByKey: this.changeDateByKey, interval: this.props.interval, loop: this.props.loop }, this.state, { yearIndex,
                    dayIndex }) }, this.props.children && this.props.children(Object.assign({ letsPlay: this.play, pause: this.pause, changeDateByKey: this.changeDateByKey, interval: this.props.interval, loop: this.props.loop }, this.state, { yearIndex,
                dayIndex })))));
    }
}
AbstractTimelineMain.defaultProps = {
    interval: 4000,
    loop: "all",
};
/**
 * 数据格式转化
 * @private
 * @param {number[]} datas
 * @returns {Array<{ year: string; children: TimelineComplexDate }>}
 */
export function dateFormat(datas) {
    return datas.map((date) => {
        const dateSplited = date.split("-");
        const year = dateSplited[0];
        const month = dateSplited[1];
        const day = dateSplited[2];
        const all = date;
        return {
            year,
            month,
            day,
            origin: all,
            key: all,
        };
    });
    // tslint:disable-next-line: max-file-line-count
}
/**
 * 抽象时间轴
 *
 * 在任何时候，有且只有一个时间正在播放中
 * @author James Zhang
 * @author yangyongheng
 * @export
 */
// tslint:disable-next-line: variable-name
export const AbstractTimeline = rerenderHoc(AbstractTimelineMain);
// tslint:disable-next-line: max-file-line-count
//# sourceMappingURL=index.js.map